import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useSession } from '../../lib/api/session-context';
import { ONBOARDING_ROUTES } from '../onboarding';

import { DisconnectSheet } from './DisconnectSheet';
import { InvitedBySection } from './InvitedBySection';
import { LanguageSection } from './LanguageSection';
import { LogoutSheet } from './LogoutSheet';
import type { TelegramView } from './TelegramSection';
import { TelegramSection } from './TelegramSection';
import type { AccountSectionView } from './TradingAccountSection';
import { TradingAccountSection } from './TradingAccountSection';
import { accountView, copiedChannelCount, telegramState } from './account-model';
import { useAccountReads } from './use-account-reads';
import { type AccountWrite, useAccountWrites } from './use-account-writes';

// A finished write is a status, not an error (S8 R26). A constant, not a text.
const STATUS_ROLE = 'status';

/**
 * Screen E11 · Account tab (specs/S8-account-tab/spec.md; mockups `Compte.html`,
 * `CompteDeconnexion.html`). Sections in the mockup's order: Telegram, trading account, language,
 * invited by (S8 R23). Each destructive action opens its sheet first and sends nothing until the
 * trader confirms (S8 R2, R8, I7). After a success both reads run again (S8 R6, R9, R22).
 */
export function AccountScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const { me, account, reload } = useAccountReads();
  const writes = useAccountWrites();
  const [sheet, setSheet] = useState<AccountWrite | null>(null);
  const [done, setDone] = useState<AccountWrite | null>(null);

  const telegram: TelegramView =
    me.status === 'pending'
      ? { kind: 'loading' }
      : me.status === 'error'
        ? { kind: 'failed', error: me.error }
        : { kind: telegramState(me.data.status) };
  const accountSection: AccountSectionView =
    account.status === 'pending'
      ? { kind: 'loading' }
      : account.status === 'error'
        ? { kind: 'failed', error: account.error }
        : accountView(account.data);
  const channels = me.status === 'success' ? copiedChannelCount(me.data) : 0;
  const positions = me.status === 'success' ? me.data.openPositions.count : null;

  const open = (write: AccountWrite): void => {
    writes.clearError();
    setDone(null);
    setSheet(write);
  };
  const close = (): void => {
    setSheet(null);
  };
  const confirm = async (write: AccountWrite): Promise<void> => {
    const ok = write === 'logout' ? await writes.logout() : await writes.disconnect();
    if (!ok) return; // S8 R21: the sheet stays open with the failure; nothing said stopped
    setSheet(null);
    setDone(write);
    reload();
  };
  const sheetError = (write: AccountWrite) =>
    writes.error?.write === write ? writes.error.error : null;

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        {t('account.title')}
      </Typography>
      {done !== null && (
        <Alert severity="success" role={STATUS_ROLE}>
          {t(
            done === 'logout'
              ? 'account.telegram.loggedOut'
              : 'account.tradingAccount.disconnected',
          )}
        </Alert>
      )}
      <TelegramSection
        view={telegram}
        onLogout={() => {
          open('logout');
        }}
        onSignIn={() => {
          void navigate(ONBOARDING_ROUTES.telegram);
        }}
        onRetry={me.refetch}
      />
      <TradingAccountSection
        view={accountSection}
        onDisconnect={() => {
          open('disconnect');
        }}
        onConnect={() => {
          void navigate(ONBOARDING_ROUTES.tradingAccount);
        }}
        onRetry={account.refetch}
      />
      <LanguageSection />
      {session.status === 'ready' && session.affiliate !== null && (
        <InvitedBySection name={session.affiliate.displayName} />
      )}
      <LogoutSheet
        open={sheet === 'logout'}
        channels={channels}
        positions={positions ?? 0}
        busy={writes.pending === 'logout'}
        error={sheetError('logout')}
        onConfirm={() => {
          void confirm('logout');
        }}
        onCancel={close}
      />
      <DisconnectSheet
        open={sheet === 'disconnect'}
        positions={positions}
        busy={writes.pending === 'disconnect'}
        error={sheetError('disconnect')}
        onConfirm={() => {
          void confirm('disconnect');
        }}
        onCancel={close}
      />
    </Stack>
  );
}
